import { useQuery } from '@tanstack/react-query'
import { hydrateAuthSession } from '@/lib/auth/hydrateFromAuthSession'
import { clearAuthPersistedStorage } from '@/lib/auth/clearStaleAuthState'
import { useAuthStore, AUTH_RESET_STATE } from '@/store/useAuthStore'
import { useSessionUiStore } from '@/store/useSessionUiStore'
import { markPerformance } from '@/utils/dev/performanceMarks'

export const authSessionBootstrapKeys = {
  all: ['auth', 'session', 'bootstrap'] as const,
  detail: (teamId: string | null) => [...authSessionBootstrapKeys.all, teamId] as const,
}

export async function runAuthSessionBootstrap(): Promise<boolean> {
  markPerformance('session-fetch-start')

  try {
    const outcome = await hydrateAuthSession()
    if (outcome.status === 'authenticated') {
      return true
    }
    // API injoignable au boot (réseau, 5xx) : garder l'état persisté plutôt que de
    // purger l'identité. RouteGuard décide la redirection à partir du store, donc
    // purger ici déconnecterait l'utilisateur sur une panne passagère.
    if (outcome.status === 'unavailable') {
      return false
    }
    useAuthStore.setState({ ...AUTH_RESET_STATE })
    useSessionUiStore.getState().clear()
    clearAuthPersistedStorage()
    return false
  } finally {
    markPerformance('session-fetch-end')
    useAuthStore.setState({ sessionBootstrapped: true })
  }
}

interface UseAuthSessionBootstrapOptions {
  enabled?: boolean
}

/**
 * Bootstrap session au reload : GET /auth/session → hydrate Zustand + seed React Query.
 * Désactivé après login/OAuth (markSessionBootstrapComplete) ou une fois terminé.
 */
export function useAuthSessionBootstrap(options?: UseAuthSessionBootstrapOptions) {
  const activeTeamId = useAuthStore((state) => state.activeTeamId)
  const sessionBootstrapped = useAuthStore((state) => state.sessionBootstrapped)
  const enabled = (options?.enabled ?? true) && !sessionBootstrapped

  return useQuery({
    queryKey: authSessionBootstrapKeys.detail(activeTeamId),
    queryFn: runAuthSessionBootstrap,
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    // HMR peut réinitialiser le store alors que le cache bootstrap est encore "success"
    refetchOnMount: enabled ? 'always' : false,
  })
}
